import React, { useContext } from 'react';
import { BreadcrumbItem } from '../utils/breadcrumbsUtilService';
import { pageTypeMap, PageType, pageTypePatterns } from '../../shared/pageTypeConstants';
import { PageRoutes } from '../../../conf/Global';

export type BreadcrumbContextType = {
    breadcrumbArray: BreadcrumbItem[];
    setBreadcrumbArray: (breadcrumbs: BreadcrumbItem[]) => void;
};

export const DEFAULT_CONTEXT: BreadcrumbContextType = {
    breadcrumbArray: [
        {
            name: 'Home Icon',
            path: PageRoutes.DEFAULT_HOME,
        },
        {
            name: 'Enterprise Data Catalog',
            path: PageRoutes.MODULE_HOME,
        },
        {
            name: 'Dashboard',
            path: PageRoutes.ROOT,
        },
    ],
    setBreadcrumbArray: (_: BreadcrumbItem[]) => null,
};

export const BreadcrumbContext = React.createContext<BreadcrumbContextType>(DEFAULT_CONTEXT);

/**
 * Get the current page type based on breadcrumbs
 */
export function getPageTypeFromBreadcrumbs(breadcrumbArray: BreadcrumbItem[]): PageType {
    if (!breadcrumbArray || breadcrumbArray.length === 0) {
        return pageTypeMap.HOME;
    }

    // Get the last breadcrumb item (current page)
    const lastBreadcrumb = breadcrumbArray[breadcrumbArray.length - 1];
    const path = lastBreadcrumb.path?.toLowerCase() || '';
    const name = lastBreadcrumb.name?.toLowerCase() || '';

    // Find the first matching pattern
    const foundType = pageTypePatterns.find(({ patterns }) =>
        patterns.some((pattern) => name.includes(pattern) || path.includes(pattern)),
    );

    return foundType ? foundType.type : pageTypeMap.HOME;
}

export function useBreadcrumbContext() {
    const context = useContext(BreadcrumbContext);
    if (context === null)
        throw new Error(`${useBreadcrumbContext.name} must be used under a BreadcrumbContextProvider`);
    return context;
}

export function useBreadcrumbArray() {
    return useBreadcrumbContext().breadcrumbArray;
}

/**
 * Hook to get the current page type based on breadcrumbs
 */
export function usePageType(): PageType {
    const breadcrumbArray = useBreadcrumbArray();
    return getPageTypeFromBreadcrumbs(breadcrumbArray);
}
